import { useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { BYO } from "@/data/menu";
import Reveal from "./Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-all active:scale-95 ${
        active
          ? "border-artichoke bg-artichoke text-cream shadow-md shadow-artichoke/25"
          : "border-chalk/15 bg-white text-chalk/70 hover:border-artichoke/50 hover:text-artichoke"
      }`}
    >
      {children}
    </button>
  );
}

function Group({ title, children }) {
  return (
    <div className="mt-7 first:mt-0">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-artichoke">
        {title}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <li className="flex items-baseline gap-2">
      <span className="shrink-0 font-bold text-cream/50">{label}</span>
      <span className="flex-1 border-b border-dotted border-cream/20" />
      <span className="max-w-[60%] text-right font-semibold">{value}</span>
    </li>
  );
}

export default function SandwichBuilder() {
  const [size, setSize] = useState("whole");
  const [bread, setBread] = useState("Dutch Crunch");
  const [meat, setMeat] = useState("Turkey");
  const [cheese, setCheese] = useState("Pepper Jack");
  const [veggies, setVeggies] = useState(["Lettuce", "Tomatoes"]);
  const [extras, setExtras] = useState([]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const total = useMemo(() => {
    const base = BYO.sizes.find((s) => s.id === size).price;
    const meatUp = BYO.meats.find((m) => m.name === meat)?.up || 0;
    const extrasSum = extras.reduce(
      (sum, name) => sum + (BYO.extras.find((e) => e.name === name)?.price || 0),
      0
    );
    return base + meatUp + extrasSum;
  }, [size, meat, extras]);

  const sizeLabel = BYO.sizes.find((s) => s.id === size).label;

  return (
    <Reveal delay={0.1}>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <Group title="1 · Pick your size">
            {BYO.sizes.map((s) => (
              <Chip
                key={s.id}
                testid={`byo-size-${s.id}`}
                active={size === s.id}
                onClick={() => setSize(s.id)}
              >
                {s.label} · ${s.price.toFixed(2)}
              </Chip>
            ))}
          </Group>
          <Group title="2 · Bread">
            {BYO.breads.map((b) => (
              <Chip
                key={b}
                testid={`byo-bread-${slug(b)}`}
                active={bread === b}
                onClick={() => setBread(b)}
              >
                {b}
              </Chip>
            ))}
          </Group>
          <Group title="3 · Meat">
            {BYO.meats.map((m) => (
              <Chip
                key={m.name}
                testid={`byo-meat-${slug(m.name)}`}
                active={meat === m.name}
                onClick={() => setMeat(m.name)}
              >
                {m.name}
                {m.up ? ` +$${m.up}` : ""}
              </Chip>
            ))}
          </Group>
          <Group title="4 · Cheese">
            {BYO.cheeses.map((c) => (
              <Chip
                key={c}
                testid={`byo-cheese-${slug(c)}`}
                active={cheese === c}
                onClick={() => setCheese(c)}
              >
                {c}
              </Chip>
            ))}
          </Group>
          <Group title="5 · Veggies & condiments">
            {BYO.veggies.map((v) => (
              <Chip
                key={v}
                testid={`byo-veggie-${slug(v)}`}
                active={veggies.includes(v)}
                onClick={() => toggle(veggies, setVeggies, v)}
              >
                {v}
              </Chip>
            ))}
          </Group>
          <Group title="6 · Extras">
            {BYO.extras.map((e) => (
              <Chip
                key={e.name}
                testid={`byo-extra-${slug(e.name)}`}
                active={extras.includes(e.name)}
                onClick={() => toggle(extras, setExtras, e.name)}
              >
                {e.name} +${e.price.toFixed(2)}
              </Chip>
            ))}
          </Group>
        </div>

        <div
          data-testid="byo-summary"
          className="h-fit rounded-3xl bg-chalk p-7 text-cream shadow-2xl shadow-chalk/30 lg:sticky lg:top-28"
        >
          <p className="font-editorial text-sm italic tracking-[0.25em] text-gold">
            YOUR BUILD
          </p>
          <ul className="mt-5 space-y-3 text-sm">
            <SummaryRow label="Size" value={sizeLabel} />
            <SummaryRow label="Bread" value={bread} />
            <SummaryRow label="Meat" value={meat} />
            <SummaryRow label="Cheese" value={cheese} />
            <SummaryRow
              label="Veggies"
              value={veggies.length ? veggies.join(", ") : "None yet"}
            />
            <SummaryRow
              label="Extras"
              value={extras.length ? extras.join(", ") : "None"}
            />
          </ul>
          <div className="mt-6 flex items-baseline justify-between border-t border-dashed border-cream/20 pt-5">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cream/60">
              Total
            </span>
            <span
              data-testid="byo-total-price"
              className="font-display text-3xl font-bold text-gold"
            >
              ${total.toFixed(2)}
            </span>
          </div>
          <a
            data-testid="byo-call-button"
            href="/order"
            className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display text-base font-semibold text-chalk transition-all hover:-translate-y-0.5 hover:brightness-105"
          >
            <Phone className="h-4 w-4" />
            Order it online now
          </a>
          <p className="mt-3 text-center text-[11px] text-cream/45">
            Build it in the online order — or call (831) 633-4259 and read it to us.
          </p>
        </div>
      </div>
    </Reveal>
  );
}
